'use client';

import React, { useState, useEffect, useRef } from 'react';
import { localeNames, locales } from "@/config/i18n";
import { cn } from '@/lib/utils';
import { ChevronDown, Globe } from 'lucide-react';

/**
 * 简化的语言选择器组件 - 移动端优先设计
 */
const SimpleLanguageSelector = ({
  variant = 'default',
  className = '',
  triggerClassName = '',
  contentClassName = '',
  showLabel = true,
  ...props
}) => {
  const [currentLocale, setCurrentLocale] = useState('en');
  const [mounted, setMounted] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // 客户端挂载后获取当前语言
  useEffect(() => {
    setMounted(true);
    const path = window.location.pathname;
    for (const loc of locales) {
      if (path === `/${loc}` || path.startsWith(`/${loc}/`)) {
        setCurrentLocale(loc);
        break;
      }
    }
  }, []);

  // 点击外部关闭下拉菜单 - 优化版本
  useEffect(() => {
    let closeTimeout;

    const handleClickOutside = (event) => {
      // 检查事件目标是否在下拉菜单内部
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        // 添加小延迟，防止快速连续点击
        closeTimeout = setTimeout(() => {
          setIsOpen(false);
        }, 50);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => {
      // 滚动时关闭下拉菜单
      if (isOpen) {
        setIsOpen(false);
      }
    };

    const handleResize = () => {
      // 窗口大小改变时关闭下拉菜单
      if (isOpen) {
        setIsOpen(false);
      }
    };

    // 移动端触摸滚动处理
    const handleTouchMove = (event) => {
      if (isOpen && variant === 'mobile') {
        // 检查触摸是否在下拉菜单内
        const dropdownElement = dropdownRef.current?.querySelector('[role="listbox"]');
        if (dropdownElement && dropdownElement.contains(event.target)) {
          // 在下拉菜单内滚动，阻止外层滚动
          event.stopPropagation();
        }
      }
    };

    if (isOpen) {
      // 只监听鼠标点击，不阻止触摸滚动
      document.addEventListener('mousedown', handleClickOutside, { capture: true, passive: false });
      document.addEventListener('keydown', handleKeyDown, { passive: false });
      window.addEventListener('scroll', handleScroll, { passive: true });
      window.addEventListener('resize', handleResize, { passive: true });
      
      // 移动端添加触摸滚动处理
      if (variant === 'mobile') {
        document.addEventListener('touchmove', handleTouchMove, { capture: true, passive: false });
      }
    }

    return () => {
      if (closeTimeout) {
        clearTimeout(closeTimeout);
      }
      document.removeEventListener('mousedown', handleClickOutside, { capture: true });
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      
      if (variant === 'mobile') {
        document.removeEventListener('touchmove', handleTouchMove, { capture: true });
      }
    };
  }, [isOpen, variant]);

  // 语言切换处理
  const handleLanguageChange = (newLocale) => {
    try {
      setCurrentLocale(newLocale);
      setIsOpen(false);
      const path = window.location.pathname;
      let newPath = path;
      
      // 移除当前语言前缀
      for (const loc of locales) {
        if (path === `/${loc}`) {
          newPath = '/';
          break;
        }
        if (path.startsWith(`/${loc}/`)) {
          newPath = path.substring(loc.length + 1);
          break;
        }
      }
      
      // 添加新语言前缀
      if (newLocale !== 'en') {
        newPath = `/${newLocale}${newPath}`;
      }
      
      window.location.href = newPath;
    } catch (error) {
      console.error('Language switch error:', error);
      window.location.reload();
    }
  };

  // 简化的样式配置
  const styles = {
    default: {
      trigger: "inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-white bg-gray-800 border border-gray-600 rounded-md hover:bg-gray-700 transition-colors duration-200",
      content: "absolute top-full mt-1 bg-gray-800 border border-gray-600 rounded-md shadow-lg min-w-[120px] z-50",
      item: "cursor-pointer px-3 py-2 text-sm text-gray-200 hover:text-white hover:bg-gray-700 transition-colors duration-150 w-full text-left"
    },
    sidebar: {
      trigger: "w-full justify-between bg-gray-800 border border-gray-600 text-white hover:bg-gray-700 transition-colors duration-200 rounded-md px-3 py-2",
      content: "absolute top-full mt-1 bg-gray-800 border border-gray-600 rounded-md shadow-lg min-w-[120px] z-50",
      item: "cursor-pointer px-3 py-2 text-sm text-gray-200 hover:text-white hover:bg-gray-700 transition-colors duration-150 w-full text-left"
    },
    mobile: {
      trigger: "w-full h-14 text-sm bg-gradient-to-r from-green-500/20 to-green-600/20 hover:from-green-500/30 hover:to-green-600/30 border border-green-400/30 hover:border-green-400/50 rounded-xl text-gray-200 hover:text-white transition-all duration-300 flex items-center justify-center px-3 shadow-lg hover:shadow-green-500/20 touch-manipulation active:scale-95 font-medium min-h-[56px] max-h-[56px]",
      content: "absolute top-full mt-1 bg-black border border-gray-600 rounded-lg shadow-lg w-full left-0 z-[10007] max-h-48 overflow-y-auto",
      item: "cursor-pointer px-3 py-2 text-sm text-gray-200 hover:text-white hover:bg-gray-800 transition-colors duration-150 w-full text-center touch-manipulation active:scale-95"
    }
  };

  const variantStyle = styles[variant] || styles.default;

  // 如果还没有挂载，显示加载状态
  if (!mounted) {
    return (
      <div className={cn(
        variantStyle.trigger,
        triggerClassName,
        className
      )}>
        <div className="w-4 h-4 bg-gray-400 rounded animate-pulse" />
      </div>
    );
  }

  return (
    <div ref={dropdownRef} className="relative">
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        onMouseDown={(e) => e.stopPropagation()}
        className={cn(
          variantStyle.trigger,
          triggerClassName,
          className,
          isOpen && "bg-gray-700"
        )}
        aria-label={`切换语言，当前：${localeNames[currentLocale]}`}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <Globe className="w-4 h-4" />
        <span className="mx-2">
          {showLabel ? 'Language' : localeNames[currentLocale]}
        </span>
        <ChevronDown 
          className={cn(
            "w-4 h-4 transition-transform duration-200",
            isOpen && "rotate-180"
          )} 
        />
      </button>
      
      {/* 简化的下拉菜单 */}
      <div
        className={cn(
          variantStyle.content,
          contentClassName,
          variant === 'mobile' ? "" : "max-h-48 overflow-y-auto",
          isOpen 
            ? "opacity-100 scale-100 translate-y-0" 
            : "opacity-0 scale-95 -translate-y-2 pointer-events-none"
        )}
        style={{
          transition: 'all 200ms ease-out',
          transformOrigin: 'top center',
          backgroundColor: variant === 'mobile' ? '#000000' : '#374151',
          opacity: isOpen ? 1 : 0,
          backdropFilter: 'none',
          ...(variant === 'mobile' && {
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain',
            touchAction: 'pan-y'
          })
        }}
        role="listbox"
        aria-label="可用语言列表"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        onTouchMove={(e) => {
          // 移动端触摸滚动时阻止事件冒泡
          if (variant === 'mobile') {
            e.stopPropagation();
          }
        }}
      >
        {Object.keys(localeNames).map((key) => (
          <button
            key={key}
            onClick={(e) => {
              e.stopPropagation();
              handleLanguageChange(key);
            }}
            onMouseDown={(e) => e.stopPropagation()}
            className={cn(
              variantStyle.item,
              currentLocale === key && "bg-gray-700 text-white font-medium"
            )}
            style={{
              backgroundColor: currentLocale === key ? '#374151' : 'transparent',
              opacity: 1,
              backdropFilter: 'none'
            }}
            role="option"
            aria-selected={currentLocale === key}
          >
            <span className="flex items-center justify-center gap-2 w-full">
              <span className="text-xs opacity-60">{key.toUpperCase()}</span>
              <span>{localeNames[key]}</span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default SimpleLanguageSelector; 